/**
 * Stash & Loadout.
 *
 * Three input paths for the same move, and all of them funnel into `commitMove`, which is the
 * only place that talks to the server: HTML5 drag-and-drop, select-then-place (a tap on the
 * stack, a tap on the square), and — new in Phase 2c — a right-click menu that does the things a
 * move cannot: wear it, take it off, throw it away, mend it.
 *
 * Moves are optimistic. The server owns inventory, so a rejected move has to snap all the
 * way back — including the displaced stack in a swap, which is why the rollback restores
 * the whole previous list rather than un-patching two entries.
 */

import type { VNode } from 'preact';
import { useState } from 'preact/hooks';
import {
  BACKPACK_SLOTS,
  CRAFTING_ENABLED,
  INSURANCE_RATE,
  ITEMS,
  ItemLocation,
  type ItemLocationKind,
  type ItemStack,
  MAX_DURABILITY,
  QUICK_SLOTS,
  SATCHEL_SLOTS,
  STASH_SLOTS,
  durabilityOf,
  isBroken,
  isEquipLocation,
  isDamageable,
  itemValue,
} from '@stillsun/shared';
import { api } from '../../net/api.js';
import { session } from '../../net/session.js';
import { putEquipment, type EquipSlot } from '../../net/progress-api.js';
import { Loadout } from './Loadout.js';
import { ItemIcon } from '../icons.js';
import {
  type HubController,
  ItemMenu,
  ItemTooltip,
  LEDGER_SILENT,
  type MenuEntry,
  Section,
  baseItemEntries,
  errText,
  loadList,
  rarityHex,
  rarityName,
  rarityRailHex,
  rarityRailIsFlat,
  useEscape,
  useLoader,
  useProgress,
  wick,
} from './common.js';

interface Group {
  location: ItemLocationKind;
  label: string;
  count: number;
  /** Grids wrap; rows stay on one line because a weapon slot beside its label reads better. */
  wide: boolean;
  /**
   * The backpack is drawn on a fixed four-column grid here as well as in the raid HUD, so the
   * twelve squares a player learns mid-raid are the same twelve squares in the same shape at home.
   */
  bag?: boolean;
}

const CARRIED: Group[] = [
  { location: ItemLocation.Primary, label: 'Primary', count: 1, wide: false },
  { location: ItemLocation.Secondary, label: 'Secondary', count: 1, wide: false },
  { location: ItemLocation.Quick, label: 'Quick', count: QUICK_SLOTS, wide: false },
  { location: ItemLocation.Satchel, label: 'Satchel', count: SATCHEL_SLOTS, wide: false },
  {
    location: ItemLocation.Backpack,
    label: 'Backpack',
    count: BACKPACK_SLOTS,
    wide: true,
    bag: true,
  },
];

const ALL_GROUPS: Group[] = [
  { location: ItemLocation.Stash, label: 'Stash', count: STASH_SLOTS, wide: true },
  ...CARRIED,
];

/**
 * Places stacks into fixed cells. Slot indices come from a server that is still being
 * written, so a duplicate or out-of-range index falls into the first free cell instead of
 * vanishing — an item the player cannot see is worse than one in the wrong square.
 */
function layout(
  items: ItemStack[],
  location: ItemLocationKind,
  count: number,
): (ItemStack | null)[] {
  const cells: (ItemStack | null)[] = new Array<ItemStack | null>(count).fill(null);
  const strays: ItemStack[] = [];
  for (const it of items) {
    if (it.location !== location) continue;
    if (it.slot >= 0 && it.slot < count && cells[it.slot] === null) cells[it.slot] = it;
    else strays.push(it);
  }
  for (const it of strays) {
    const free = cells.indexOf(null);
    if (free < 0) break;
    cells[free] = it;
  }
  return cells;
}

/** The paperdoll slot a stack belongs in. Only ever asked of something already equippable. */
const equipSlotOf = (stack: ItemStack): EquipSlot => {
  const def = ITEMS[stack.itemId];
  if (def?.kind === 'weapon') return 'weapon';
  return def?.armorSlot ?? 'chest';
};

function firstFree(
  items: ItemStack[],
  location: ItemLocationKind,
  count: number,
  ignoreUid: number,
): number {
  for (let i = 0; i < count; i++) {
    const taken = items.some(
      (it) => it.uid !== ignoreUid && it.location === location && it.slot === i,
    );
    if (!taken) return i;
  }
  return -1;
}

export interface StashProps {
  ctl: HubController;
  nonce: number;
  token: string;
}

export function Stash(props: StashProps): VNode {
  const { token } = props;
  const loaded = useLoader<ItemStack[]>(
    () => loadList<ItemStack>('/api/stash', token, 'items'),
    `stash:${String(props.nonce)}:${token}`,
  );
  const [selUid, setSelUid] = useState<number | null>(null);
  const [dragUid, setDragUid] = useState<number | null>(null);
  const [hover, setHover] = useState<{ stack: ItemStack; x: number; y: number } | null>(null);
  const [menu, setMenu] = useState<{ uid: number; x: number; y: number } | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // The paperdoll and the context menu both refuse what the server would refuse — a piece for
  // another Calling, or one the character has not grown into — so the reason arrives instantly
  // rather than after a round trip that was always going to say no.
  const progress = useProgress(token, props.nonce);
  const calling = progress.data?.calling ?? null;
  const level = progress.data?.level ?? null;

  const items = loaded.data;
  const selected = items?.find((it) => it.uid === selUid) ?? null;
  const menuStack = menu === null ? null : (items?.find((it) => it.uid === menu.uid) ?? null);

  // ESC peels one layer: the menu first, then the tooltip and the inspector.
  useEscape(props.ctl, menu !== null, () => {
    setMenu(null);
  });
  useEscape(props.ctl, menu === null && (selected !== null || hover !== null), () => {
    setHover(null);
    setSelUid(null);
  });

  const commitMove = (uid: number, location: ItemLocationKind, slot: number): void => {
    const list = items;
    if (!list || busy) return;
    const src = list.find((it) => it.uid === uid);
    if (!src) return;
    if (src.location === location && src.slot === slot) {
      setSelUid(null);
      return;
    }
    const occupant =
      list.find((it) => it.uid !== uid && it.location === location && it.slot === slot) ?? null;

    const next = list.map((it) => {
      if (it.uid === src.uid) return { ...it, location, slot };
      if (occupant && it.uid === occupant.uid)
        return { ...it, location: src.location, slot: src.slot };
      return it;
    });
    loaded.set(next);
    setSelUid(null);
    setNote('');
    setBusy(true);

    void (async () => {
      try {
        const res = await api<{ merged?: boolean; items?: ItemStack[] }>('/api/stash/move', {
          body: { uid: src.uid, location, slot },
          token,
        });
        // A stackable stack dropped onto a matching one merges rather than swaps (the owner's
        // stacking rule). There is then no occupant to send back — firing the second half of the
        // swap would address a uid the server has just deleted — and the optimistic list above is
        // wrong about both rows, so the server's answer replaces it wholesale.
        if (res.merged === true) {
          if (res.items) loaded.set(res.items);
          else loaded.reload();
          return;
        }
        if (occupant) {
          await api('/api/stash/move', {
            body: { uid: occupant.uid, location: src.location, slot: src.slot },
            token,
          });
        }
      } catch (err) {
        loaded.set(list);
        setNote(errText(err, 'That move did not take.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  /**
   * Right-click Drop, at home. The item is destroyed, not put anywhere — the same verb the world
   * spends `InteractAction.Destroy` on. `ItemMenu` has already taken the Rare+ confirm; by the
   * time this runs the player has said yes twice.
   */
  const destroy = (stack: ItemStack): void => {
    const list = items;
    if (!list || busy) return;
    loaded.set(list.filter((it) => it.uid !== stack.uid));
    setSelUid(null);
    setNote('');
    setBusy(true);
    void (async () => {
      try {
        const res = await api<{ items?: ItemStack[] }>('/api/stash/destroy', {
          body: { uid: stack.uid },
          token,
        });
        if (res.items) loaded.set(res.items);
        else loaded.reload();
      } catch (err) {
        loaded.set(list);
        setNote(errText(err, 'It is still there.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  const insure = (stack: ItemStack): void => {
    const list = items;
    if (!list || busy || stack.insured) return;
    loaded.set(list.map((it) => (it.uid === stack.uid ? { ...it, insured: true } : it)));
    setNote('');
    setBusy(true);
    void (async () => {
      try {
        await api('/api/insure', { body: { uid: stack.uid }, token });
        // The premium comes out of Wick, so the header is stale until the profile reloads.
        await session.refresh();
      } catch (err) {
        loaded.set(list);
        setNote(errText(err, 'The Ferry would not write that policy.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  /**
   * The menu's Wear / Take off. `PUT /api/equipment` is the same write the paperdoll makes — one
   * path, one set of refusals — and the grid is re-read afterwards because equipping displaces
   * whatever was in the slot into the backpack.
   */
  const equipTo = (stack: ItemStack, slot: EquipSlot | null): void => {
    if (busy) return;
    setMenu(null);
    setNote('');
    setBusy(true);
    // `slot === null` is the unequip, and it still has to name a slot for the route to address.
    // The name comes from the item's own `armorSlot` — the slot it *belongs* in — rather than
    // from the location it currently occupies. Those are the same thing for every item in the
    // table (nothing is wearable in two places), and the server re-checks either way.
    void putEquipment(token, slot ?? equipSlotOf(stack), slot === null ? null : stack.uid).then(
      () => {
        setBusy(false);
        loaded.reload();
      },
      (err: unknown) => {
        setBusy(false);
        setNote(errText(err, 'The quartermaster would not take that.'));
      },
    );
  };

  const activate = (location: ItemLocationKind, slot: number, stack: ItemStack | null): void => {
    if (selUid !== null) {
      if (stack && stack.uid === selUid) {
        setSelUid(null);
        return;
      }
      commitMove(selUid, location, slot);
      return;
    }
    setSelUid(stack ? stack.uid : null);
  };

  const onSlotDrop = (location: ItemLocationKind, slot: number, uid: number | null): void => {
    const moving = uid ?? dragUid;
    setDragUid(null);
    if (moving !== null) commitMove(moving, location, slot);
  };

  if (loaded.loading)
    return (
      <div class="hub-pane">
        <p class="hub-notice">Counting the stash…</p>
      </div>
    );
  if (items === null) {
    return (
      <div class="hub-pane">
        <p class="hub-notice">{LEDGER_SILENT}</p>
      </div>
    );
  }

  const renderGroup = (g: Group): VNode => {
    const cells = layout(items, g.location, g.count);
    return (
      <div key={g.label} class={`hub-group${g.wide ? ' wide' : ''}${g.bag === true ? ' bag' : ''}`}>
        <h3 class="hub-group-title">
          {g.label}
          <span class="hub-hint">{`${String(cells.filter((c) => c !== null).length)}/${String(g.count)}`}</span>
        </h3>
        <div class="hub-slots">
          {cells.map((stack, i) => (
            <Slot
              key={`${String(g.location)}:${String(i)}`}
              stack={stack}
              location={g.location}
              slot={i}
              selected={stack !== null && stack.uid === selUid}
              dragging={stack !== null && stack.uid === dragUid}
              onActivate={activate}
              onSlotDrop={onSlotDrop}
              onDragBegin={setDragUid}
              onHover={(s, x, y) => {
                setHover(s ? { stack: s, x, y } : null);
              }}
              onContext={(s, x, y) => {
                setHover(null);
                setMenu({ uid: s.uid, x, y });
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div class="hub-pane hub-pane-stash">
      {note.length > 0 ? <p class="hub-error">{note}</p> : null}
      <div class="hub-steptop">
        <p class="hub-notice">
          Drag a stack, or tap it and tap where it should go. Right-click anything to wear it, take
          it off, or read what it is worth. The satchel is the only thing you keep when you go down.
        </p>
        {/* Phase 2c: the bench is dark until the recipe table is re-cut, so the door to it is
            not drawn rather than drawn onto a tab that would refuse to open. */}
        {CRAFTING_ENABLED ? (
          <button
            type="button"
            class="hub-btn hub-btn-quiet"
            onClick={() => {
              props.ctl.setTab('workbench');
            }}
          >
            Workbench
          </button>
        ) : null}
      </div>

      <div class="hub-cols">
        <Section title="Stash" hint={`${String(STASH_SLOTS)} slots at home`}>
          {renderGroup({
            location: ItemLocation.Stash,
            label: 'Stash',
            count: STASH_SLOTS,
            wide: true,
          })}
        </Section>

        <Section title="Carried" hint="What drops with you">
          {CARRIED.map(renderGroup)}
        </Section>
      </div>

      {/* One Section, two blocks: the paperdoll and the weapon rails. They share a source
          (`items`) and a consequence — equipping or fitting moves item rows, so both ask for
          the grid above to be re-read — and splitting them into two Sections put a heading
          between a piece and the square it was dragged from. */}
      <Section title="Loadout" hint="What you wear and what you swing">
        <Loadout
          items={items}
          token={token}
          calling={calling}
          level={level}
          onChanged={() => {
            loaded.reload();
          }}
          onContext={(stack, x, y) => {
            setHover(null);
            setMenu({ uid: stack.uid, x, y });
          }}
          onHover={(stack, x, y) => {
            setHover(stack ? { stack, x, y } : null);
          }}
        />
      </Section>

      {selected ? (
        <Inspector
          stack={selected}
          items={items}
          busy={busy}
          onInsure={() => {
            insure(selected);
          }}
          onMoveTo={(location, count) => {
            const slot = firstFree(items, location, count, selected.uid);
            if (slot < 0) setNote('Nowhere free there.');
            else commitMove(selected.uid, location, slot);
          }}
          onClose={() => {
            setSelUid(null);
          }}
        />
      ) : null}

      {menu !== null && menuStack !== null ? (
        <ItemMenu
          stack={menuStack}
          x={menu.x}
          y={menu.y}
          entries={stashMenuEntries(menuStack, level, calling, equipTo, destroy)}
          onClose={() => {
            setMenu(null);
          }}
        />
      ) : null}

      {menu === null && hover && (!selected || selected.uid !== hover.stack.uid) ? (
        <ItemTooltip stack={hover.stack} x={hover.x} y={hover.y} level={level} calling={calling} />
      ) : null}
    </div>
  );
}

/**
 * The hub's menu rows.
 *
 * Drop destroys, here as in the world: `POST /api/stash/destroy` is the home half of
 * `InteractAction.Destroy`, and the confirm step in `ItemMenu` is what stands between a Rare and
 * an accident. It is refused on a worn piece — take it off first — because "I meant to bin my
 * only chest plate while wearing it" is not a sentence anybody finishes.
 *
 * Repair is appended by the vendor panel, which is the only place it means anything.
 */
function stashMenuEntries(
  stack: ItemStack,
  level: number | null,
  calling: number | null,
  equipTo: (stack: ItemStack, slot: EquipSlot | null) => void,
  destroy: (stack: ItemStack) => void,
): MenuEntry[] {
  const entries = baseItemEntries({
    stack,
    level,
    calling,
    onEquip: (s) => {
      equipTo(s, equipSlotOf(s));
    },
    onUnequip: (s) => {
      equipTo(s, null);
    },
    onDrop: destroy,
  });
  return entries.map((entry) =>
    entry.key === 'drop' && isEquipLocation(stack.location)
      ? { ...entry, reason: 'Take it off first' }
      : entry,
  );
}

// --------------------------------------------------------------------- slot

interface SlotProps {
  stack: ItemStack | null;
  location: ItemLocationKind;
  slot: number;
  selected: boolean;
  dragging: boolean;
  onActivate: (location: ItemLocationKind, slot: number, stack: ItemStack | null) => void;
  onSlotDrop: (location: ItemLocationKind, slot: number, uid: number | null) => void;
  onDragBegin: (uid: number | null) => void;
  onHover: (stack: ItemStack | null, x: number, y: number) => void;
  onContext: (stack: ItemStack, x: number, y: number) => void;
}

function Slot(props: SlotProps): VNode {
  const { stack } = props;
  const def = stack ? ITEMS[stack.itemId] : undefined;
  const colour = def ? rarityHex(def.rarity) : 'transparent';
  const wears = isDamageable(def);
  const dur = stack === null ? MAX_DURABILITY : durabilityOf(stack);
  const broken = stack !== null && isBroken(stack);
  // Common's own colour (moon) is also this panel's bright text colour, so its rail is drawn
  // in wine-2 instead — a solid line rather than the usual translucent glow, and a `var()`
  // token cannot take an `33` alpha suffix anyway. Every other tier, Legendary's orange
  // included, gets its own colour and the usual glow. (M6 inverted this: see `rarityRailHex`.)
  const rail = def ? rarityRailHex(def.rarity) : 'transparent';
  const railShadow = def !== undefined && rarityRailIsFlat(def.rarity) ? rail : `${rail}33`;

  return (
    <div
      class={`hub-slot${stack ? ' filled' : ''}${props.selected ? ' sel' : ''}${props.dragging ? ' drag' : ''}${broken ? ' broken' : ''}`}
      draggable={stack !== null}
      role="button"
      tabIndex={0}
      aria-label={
        def ? `${def.name} — ${rarityName(def.rarity)}${broken ? ', broken' : ''}` : 'Empty slot'
      }
      // The rarity rail is inline, so it would beat `.broken`'s border in the cascade — a ruined
      // Epic would keep its purple edge. Broken wins: it is the more urgent of the two.
      style={
        stack
          ? {
              borderColor: broken ? 'var(--danger)' : rail,
              boxShadow: `inset 0 0 0 1px ${broken ? 'var(--danger)' : railShadow}`,
            }
          : {}
      }
      onClick={() => {
        props.onActivate(props.location, props.slot, stack);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        if (stack) props.onContext(stack, e.clientX, e.clientY);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          props.onActivate(props.location, props.slot, stack);
        }
      }}
      onMouseEnter={(e) => {
        if (stack) props.onHover(stack, e.clientX, e.clientY);
      }}
      onMouseMove={(e) => {
        if (stack) props.onHover(stack, e.clientX, e.clientY);
      }}
      onMouseLeave={() => {
        props.onHover(null, 0, 0);
      }}
      onDragStart={(e) => {
        if (!stack) return;
        props.onDragBegin(stack.uid);
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', String(stack.uid));
        }
      }}
      onDragEnd={() => {
        props.onDragBegin(null);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const raw = e.dataTransfer?.getData('text/plain') ?? '';
        const uid = /^\d+$/.test(raw) ? Number(raw) : null;
        props.onSlotDrop(props.location, props.slot, uid);
      }}
    >
      {stack && def ? (
        <>
          <ItemIcon def={def} size={30} {...(wears ? { durability: dur } : {})} />
          <span class="hub-slot-name" style={{ color: colour }}>
            {def.name}
          </span>
          {stack.qty > 1 ? <span class="hub-slot-qty">{String(stack.qty)}</span> : null}
          {stack.insured ? <span class="hub-slot-ins" title="Insured" /> : null}
        </>
      ) : null}
      {stack && !def ? <span class="hub-slot-name">{stack.itemId}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- inspector

interface InspectorProps {
  stack: ItemStack;
  items: ItemStack[];
  busy: boolean;
  onInsure: () => void;
  onMoveTo: (location: ItemLocationKind, count: number) => void;
  onClose: () => void;
}

function Inspector(props: InspectorProps): VNode {
  const { stack } = props;
  const def = ITEMS[stack.itemId];
  const value = itemValue(stack);
  const premium = Math.ceil(value * INSURANCE_RATE);

  return (
    <aside class="hub-inspector" role="dialog" aria-label="Item">
      <div class="hub-inspector-head">
        <b style={{ color: def ? rarityHex(def.rarity) : 'var(--bone)' }}>
          {def?.name ?? stack.itemId}
        </b>
        <button type="button" class="hub-btn hub-btn-quiet" onClick={props.onClose}>
          Close
        </button>
      </div>
      <span class="hub-tip-meta">
        {def ? `${rarityName(def.rarity)} · ${def.kind}` : 'Unrecognised'} ·{' '}
        {stack.qty > 1 ? `${String(stack.qty)} × ` : ''}
        {wick(value)} Wick
      </span>
      {def ? <p class="hub-inspector-desc">{def.desc}</p> : null}

      <div class="hub-inspector-row">
        {stack.insured ? (
          <span class="hub-insured">
            Insured — returns 10 minutes after the raid if nobody loots it.
          </span>
        ) : (
          <button
            type="button"
            class="hub-btn hub-btn-amber"
            disabled={props.busy || premium <= 0}
            onClick={props.onInsure}
          >
            {`Insure · ${wick(premium)} Wick`}
          </button>
        )}
      </div>

      <div class="hub-inspector-row">
        <span class="hub-label">Move to</span>
        <div class="hub-chips">
          {ALL_GROUPS.filter((g) => g.location !== stack.location).map((g) => {
            const free = firstFree(props.items, g.location, g.count, stack.uid) >= 0;
            return (
              <button
                key={g.label}
                type="button"
                class="hub-chip"
                disabled={props.busy || !free}
                onClick={() => {
                  props.onMoveTo(g.location, g.count);
                }}
              >
                {g.label}
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
